import React, { useEffect, useMemo, useState } from 'react';

import { useGetDepartment, useGetHandler } from '@vlabs/api/artemy/client';
import type { HandlerRecord } from '@vlabs/api/artemy/model';
import { DetectionIcon } from '@vlabs/icons';
import { Page, PageContent } from '@vlabs/monokit/shared/ui/page';
import { SideNavLayout } from '@vlabs/monokit/shared/ui/side-nav-layout';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import { SideMenu, Tag } from '@vlabs/uikit';
import { Link, useParams } from 'react-router-dom';

import st from './HandlerViewPage.module.sass';
import { HandlerViewPageHeader } from './HandlerViewPageHeader';
import { useHandlerViewPageTranslation } from './i18n';
import {
  CallbacksSection, DetectionSection, MatchSection, StorageSection, TagsSection,
} from './sections';
import {
  hasCallbacksContent, hasDetectionContent, hasMatchContent, hasStorageContent, hasTagsContent,
} from './viewModel';
import { routes } from '../../../app/routes';

type SectionKey = 'detection' | 'match' | 'storage' | 'tags' | 'callbacks';

const SECTION_KEYS: SectionKey[] = ['detection', 'match', 'storage', 'tags', 'callbacks'];

const SECTION_CONTENT_CHECKERS: Record<SectionKey, (handler: HandlerRecord) => boolean> = {
  detection: hasDetectionContent,
  match: hasMatchContent,
  storage: hasStorageContent,
  tags: hasTagsContent,
  callbacks: hasCallbacksContent,
};

interface HandlerInfoCardProps {
  handler: HandlerRecord;
}

function HandlerInfoCard({ handler }: HandlerInfoCardProps): React.JSX.Element {
  const { t } = useHandlerViewPageTranslation();
  const { data: department } = useGetDepartment(handler.owner_department_id);

  return (
    <div className={st.InfoCard}>
      <div className={st.InfoTitle}>
        <span className={st.InfoIcon}>
          <DetectionIcon />
        </span>
        <span className={st.InfoName}>{handler.description}</span>
      </div>
      <div className={st.OwnerRow}>
        <span className={st.OwnerLabel}>
          {t('owner_department')}
          :
        </span>
        <Tag
          kind="neutral"
          shape="square"
          value={department?.name ?? handler.owner_department_id}
        />
      </div>
    </div>
  );
}

function HandlerViewPage(): React.JSX.Element {
  const { t } = useHandlerViewPageTranslation();
  const { handlerId } = useParams<{ handlerId: string }>();
  const { data: handler, isLoading, isError } = useGetHandler(handlerId ?? '', {
    query: { enabled: !!handlerId },
  });
  const [activeSection, setActiveSection] = useState<SectionKey>('detection');

  const sections = useMemo(() => {
    if (!handler) return [];

    return SECTION_KEYS
      .filter((key) => SECTION_CONTENT_CHECKERS[key](handler))
      .map((key) => ({ key, title: t(`sections.${key}`) }));
  }, [handler, t]);

  useEffect(() => {
    if (sections.some((section) => section.key === activeSection)) return;
    const first = sections[0];
    if (first) setActiveSection(first.key);
  }, [activeSection, sections]);

  if (isLoading) {
    return (
      <Page>
        <PageContent>
          <Loader />
        </PageContent>
      </Page>
    );
  }

  if (isError || !handler) {
    return (
      <Page>
        <PageContent>
          <div className={st.ErrorBlock}>
            <span>{t('load_error')}</span>
            <Link to={routes.handlers.all}>{t('back_to_list')}</Link>
          </div>
        </PageContent>
      </Page>
    );
  }

  let mainContent: React.JSX.Element | null;
  switch (activeSection) {
    case 'match':
      mainContent = <MatchSection handler={handler} />;
      break;
    case 'storage':
      mainContent = <StorageSection handler={handler} />;
      break;
    case 'tags':
      mainContent = <TagsSection handler={handler} />;
      break;
    case 'callbacks':
      mainContent = <CallbacksSection handler={handler} />;
      break;
    case 'detection':
    default:
      mainContent = <DetectionSection handler={handler} />;
  }

  return (
    <Page>
      <PageContent>
        <HandlerViewPageHeader handlerId={handlerId ?? ''} />

        <SideNavLayout
          info={<HandlerInfoCard handler={handler} />}
          main={<div className={st.MainScroll}>{mainContent}</div>}
          nav={sections.map((section) => (
            <SideMenu.MenuItem
              data-testid={`handler-view-nav-${section.key}`}
              isActive={activeSection === section.key}
              key={section.key}
              onClick={() => setActiveSection(section.key)}
              value={section.title}
            />
          ))}
        />
      </PageContent>
    </Page>
  );
}

export { HandlerViewPage };
